import { apiUrl } from "../taskpane";

const parseResponse = async (response) => {
  if (response.status === 204) return null;
  const text = await response.text();
  let data = null;
  if (text) {
    try { data = JSON.parse(text); } catch { data = text; }
  }
  if (!response.ok) {
    const detail = (data && data.detail) || response.statusText || "Request failed";
    const err = new Error(typeof detail === "string" ? detail : JSON.stringify(detail));
    err.status = response.status;
    err.payload = data;
    throw err;
  }
  return data;
};

const authHeaders = (token, json = false) => {
  const h = { Authorization: `Bearer ${token}` };
  if (json) h["Content-Type"] = "application/json";
  return h;
};

const tierPrefix = (tier) => `tier${tier === 0 || tier === "0" ? 0 : 1}`;

export async function fetchTier1Catalogs(token, tier = 1) {
  const res = await fetch(apiUrl(`${tierPrefix(tier)}/catalogs`), { headers: authHeaders(token) });
  return parseResponse(res);
}

export async function createTier1Catalog(token, payload, tier = 1) {
  const res = await fetch(apiUrl(`${tierPrefix(tier)}/catalogs`), {
    method: "POST",
    headers: authHeaders(token, true),
    body: JSON.stringify(payload),
  });
  return parseResponse(res);
}

export async function updateTier1Catalog(token, id, payload, tier = 1) {
  const res = await fetch(apiUrl(`${tierPrefix(tier)}/catalogs/${id}`), {
    method: "PATCH",
    headers: authHeaders(token, true),
    body: JSON.stringify(payload),
  });
  return parseResponse(res);
}

export async function deleteTier1Catalog(token, id, tier = 1) {
  const res = await fetch(apiUrl(`${tierPrefix(tier)}/catalogs/${id}`), {
    method: "DELETE",
    headers: authHeaders(token),
  });
  return parseResponse(res);
}

export async function fetchTier1BaseWorks(token, catalogId, { q = "", groupCode = "", limit = 500, tier = 1 } = {}) {
  const params = new URLSearchParams();
  if (q) params.set("q", q);
  if (groupCode) params.set("group_code", groupCode);
  if (limit) params.set("limit", String(limit));
  const qs = params.toString() ? `?${params.toString()}` : "";
  const res = await fetch(apiUrl(`${tierPrefix(tier)}/catalogs/${catalogId}/base-works${qs}`), {
    headers: authHeaders(token),
  });
  return parseResponse(res);
}

export async function fetchTier1Sheets(token, catalogId, baseWorkIds = [], priceListId = null, tier = 1) {
  const params = new URLSearchParams();
  if (baseWorkIds && baseWorkIds.length) params.set("base_work_ids", baseWorkIds.join(","));
  if (priceListId) params.set("price_list_id", String(priceListId));
  const qs = params.toString() ? `?${params.toString()}` : "";
  const res = await fetch(apiUrl(`${tierPrefix(tier)}/catalogs/${catalogId}/sheets${qs}`), {
    headers: authHeaders(token),
  });
  return parseResponse(res);
}

export async function saveTier1Sheets(token, catalogId, bundle, tier = 1) {
  const res = await fetch(apiUrl(`${tierPrefix(tier)}/catalogs/${catalogId}/sheets`), {
    method: "POST",
    headers: authHeaders(token, true),
    body: JSON.stringify(bundle),
  });
  return parseResponse(res);
}

export async function fetchTier1BaseWorkResources(token, catalogId, baseWorkId, priceListId = null, tier = 1) {
  const params = new URLSearchParams();
  if (priceListId) params.set("price_list_id", String(priceListId));
  const qs = params.toString() ? `?${params.toString()}` : "";
  const res = await fetch(
    apiUrl(`${tierPrefix(tier)}/catalogs/${catalogId}/base-works/${baseWorkId}/resources${qs}`),
    { headers: authHeaders(token) }
  );
  return parseResponse(res);
}

export async function fetchTier1MainTable(token, catalogId, tier = 1) {
  const res = await fetch(apiUrl(`${tierPrefix(tier)}/catalogs/${catalogId}/main-table`), {
    headers: authHeaders(token),
  });
  return parseResponse(res);
}

export async function saveTier1MainTable(token, catalogId, rows, tier = 1) {
  const res = await fetch(apiUrl(`${tierPrefix(tier)}/catalogs/${catalogId}/main-table`), {
    method: "POST",
    headers: authHeaders(token, true),
    body: JSON.stringify({ rows }),
  });
  return parseResponse(res);
}

export async function fetchTier1PriceLists(token, catalogId, tier = 1) {
  const res = await fetch(apiUrl(`${tierPrefix(tier)}/catalogs/${catalogId}/price-lists`), {
    headers: authHeaders(token),
  });
  return parseResponse(res);
}

export async function createTier1PriceList(token, catalogId, payload, tier = 1) {
  const res = await fetch(apiUrl(`${tierPrefix(tier)}/catalogs/${catalogId}/price-lists`), {
    method: "POST",
    headers: authHeaders(token, true),
    body: JSON.stringify(payload),
  });
  return parseResponse(res);
}

// ── Base-work requirements (bridge: works ↔ resource catalog v2) ────

export async function fetchBaseWorkRequirements(token, baseWorkId, tier = 1) {
  const res = await fetch(
    apiUrl(`${tierPrefix(tier)}/base-works/${baseWorkId}/requirements`),
    { headers: authHeaders(token) }
  );
  return parseResponse(res);
}

export async function createBaseWorkRequirement(token, baseWorkId, payload, tier = 1) {
  const res = await fetch(
    apiUrl(`${tierPrefix(tier)}/base-works/${baseWorkId}/requirements`),
    {
      method: "POST",
      headers: authHeaders(token, true),
      body: JSON.stringify(payload),
    }
  );
  return parseResponse(res);
}

export async function updateBaseWorkRequirement(token, baseWorkId, reqId, payload, tier = 1) {
  const res = await fetch(
    apiUrl(`${tierPrefix(tier)}/base-works/${baseWorkId}/requirements/${reqId}`),
    {
      method: "PATCH",
      headers: authHeaders(token, true),
      body: JSON.stringify(payload),
    }
  );
  return parseResponse(res);
}

export async function deleteBaseWorkRequirement(token, baseWorkId, reqId, tier = 1) {
  const res = await fetch(
    apiUrl(`${tierPrefix(tier)}/base-works/${baseWorkId}/requirements/${reqId}`),
    {
      method: "DELETE",
      headers: authHeaders(token),
    }
  );
  return parseResponse(res);
}

export async function resolveBaseWorkRequirements(token, baseWorkId, tier = 1) {
  const res = await fetch(
    apiUrl(`${tierPrefix(tier)}/base-works/${baseWorkId}/resolved`),
    { headers: authHeaders(token) }
  );
  return parseResponse(res);
}

export async function fetchCatalogRequirements(token, catalogId, tier = 1) {
  const res = await fetch(
    apiUrl(`${tierPrefix(tier)}/catalogs/${catalogId}/requirements`),
    { headers: authHeaders(token) }
  );
  return parseResponse(res);
}

export async function bulkCatalogRequirements(token, catalogId, rows, tier = 1) {
  const res = await fetch(
    apiUrl(`${tierPrefix(tier)}/catalogs/${catalogId}/requirements/bulk`),
    {
      method: "POST",
      headers: authHeaders(token, true),
      body: JSON.stringify({ rows }),
    }
  );
  return parseResponse(res);
}

// ── Work Group CRUD ──────────────────────────────────────────────────────────

export async function fetchTier1WorkGroups(token, catalogId, tier = 1) {
  const res = await fetch(apiUrl(`${tierPrefix(tier)}/catalogs/${catalogId}/work-groups`), {
    headers: authHeaders(token),
  });
  return parseResponse(res);
}

export async function createTier1WorkGroup(token, catalogId, payload, tier = 1) {
  const res = await fetch(apiUrl(`${tierPrefix(tier)}/catalogs/${catalogId}/work-groups`), {
    method: "POST",
    headers: authHeaders(token, true),
    body: JSON.stringify(payload),
  });
  return parseResponse(res);
}

export async function updateTier1WorkGroup(token, catalogId, wgId, payload, tier = 1) {
  const res = await fetch(apiUrl(`${tierPrefix(tier)}/catalogs/${catalogId}/work-groups/${wgId}`), {
    method: "PATCH",
    headers: authHeaders(token, true),
    body: JSON.stringify(payload),
  });
  return parseResponse(res);
}

export async function deleteTier1WorkGroup(token, catalogId, wgId, tier = 1) {
  const res = await fetch(apiUrl(`${tierPrefix(tier)}/catalogs/${catalogId}/work-groups/${wgId}`), {
    method: "DELETE",
    headers: authHeaders(token),
  });
  return parseResponse(res);
}

// ── Base Work individual CRUD ────────────────────────────────────────────────

export async function createTier1BaseWork(token, catalogId, payload, tier = 1) {
  const res = await fetch(apiUrl(`${tierPrefix(tier)}/catalogs/${catalogId}/base-works`), {
    method: "POST",
    headers: authHeaders(token, true),
    body: JSON.stringify(payload),
  });
  return parseResponse(res);
}

export async function updateTier1BaseWork(token, bwId, payload, tier = 1) {
  const res = await fetch(apiUrl(`${tierPrefix(tier)}/base-works/${bwId}`), {
    method: "PATCH",
    headers: authHeaders(token, true),
    body: JSON.stringify(payload),
  });
  return parseResponse(res);
}

export async function deleteTier1BaseWork(token, bwId, tier = 1) {
  const res = await fetch(apiUrl(`${tierPrefix(tier)}/base-works/${bwId}`), {
    method: "DELETE",
    headers: authHeaders(token),
  });
  return parseResponse(res);
}
